import { formatRecordedDate, recordedBooleanLabel } from '@/utils/recordedValues'
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Activity, Building2, History, Plus, RefreshCw, Users, Trash2 } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { CANONICAL_ROLES, getRoleLabel, type CanonicalUserRole } from '@/security/accessContract'
import {
  platformApi,
  type PlatformAuditEvent,
  type PlatformTenant,
  type PlatformUser,
  type PlatformUserSession,
} from '@/services/platform/platform.http'
import {
  CreateTenantDialog,
  CreateUserDialog,
  EditTenantDialog,
  EditUserDialog,
} from './PlatformAdminDialogs'
import TenantUsersSection from './TenantUsersSection'
import ConfirmDeleteDialog, { type DeleteMode } from '@/components/ui/ConfirmDeleteDialog'

function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description: string
  actions?: ReactNode
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">{title}</h1>
        <p className="text-text-secondary">{description}</p>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  )
}

function State({ loading, error, retry }: { loading: boolean; error: string; retry?: () => void }) {
  if (loading) return <p className="text-text-secondary">Loading…</p>
  if (error)
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p>{error}</p>
        {retry && (
          <button className="btn btn-sm mt-3" onClick={retry}>
            Retry
          </button>
        )}
      </div>
    )
  return null
}

function Status({ value }: { value: string }) {
  const tone =
    value === 'active' || value === 'healthy'
      ? 'bg-green-100 text-green-800'
      : value === 'pending' || value === 'degraded'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-slate-100 text-slate-700'
  return <span className={`rounded-full px-2 py-1 text-xs font-medium ${tone}`}>{value}</span>
}

export function PlatformOverviewPage() {
  const [tenants, setTenants] = useState(0)
  const [users, setUsers] = useState(0)
  const [audit, setAudit] = useState<PlatformAuditEvent[]>([])
  const [health, setHealth] = useState<{
    healthy: number
    degraded: number
    unavailable: number
  } | null>(null)
  const [deletions, setDeletions] = useState({ total: 0, purge_eligible: 0, on_hold: 0, failed: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const results = await Promise.allSettled([
      platformApi.listTenants({ page: 1, page_size: 1 }),
      platformApi.listUsers({ page: 1, page_size: 1 }),
      platformApi.listAudit({ page: 1, page_size: 5 }),
      platformApi.monitoringSummary(),
      platformApi.deletionSummary(),
    ])
    if (results[0].status === 'fulfilled') setTenants(results[0].value.total)
    if (results[1].status === 'fulfilled') setUsers(results[1].value.total)
    if (results[2].status === 'fulfilled') setAudit(results[2].value.items)
    if (results[3].status === 'fulfilled') setHealth(results[3].value.counts)
    if (results[4].status === 'fulfilled') setDeletions(results[4].value)
    const failures = results.filter(result => result.status === 'rejected')
    if (failures.length)
      setError(
        `${failures.length} overview source${failures.length === 1 ? '' : 's'} could not be loaded.`
      )
    setLoading(false)
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  const cards = [
    { label: 'Tenants', value: tenants, path: '/platform/tenants', icon: Building2 },
    { label: 'Users', value: users, path: '/platform/users', icon: Users },
    {
      label: 'Healthy services',
      value: health?.healthy ?? '—',
      path: '/platform/monitoring',
      icon: Activity,
    },
    {
      label: 'Service issues',
      value: health ? health.degraded + health.unavailable : '—',
      path: '/platform/monitoring',
      icon: History,
    },
    { label: 'Deleted records', value: deletions.total, path: '/platform/deletions', icon: Trash2 },
    {
      label: 'Purge eligible',
      value: deletions.purge_eligible,
      path: '/platform/deletions?status=purge_requested',
      icon: Trash2,
    },
    {
      label: 'Retention holds',
      value: deletions.on_hold,
      path: '/platform/deletions',
      icon: History,
    },
    {
      label: 'Purge failures',
      value: deletions.failed,
      path: '/platform/deletions?status=purge_failed',
      icon: Activity,
    },
  ]
  return (
    <>
      <PageHeader
        title="Platform Overview"
        description="Live tenant, identity, audit, and service-health control plane."
        actions={
          <button className="btn" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        }
      />
      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, path, icon: Icon }) => (
          <Link to={path} className="card p-5 hover:ring-2 hover:ring-primary" key={label}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">{label}</span>
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div className="mt-3 text-3xl font-bold text-text-primary">{loading ? '…' : value}</div>
          </Link>
        ))}
      </div>
      <section className="card mt-6 p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-text-primary">Recent platform activity</h2>
            <p className="text-sm text-text-secondary">
              Administrative changes across tenants and users.
            </p>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => window.location.assign('/platform/audit')}
          >
            View audit
          </button>
        </div>
        {audit.length === 0 ? (
          <p className="text-sm text-text-secondary">No platform events recorded yet.</p>
        ) : (
          <div className="divide-y divide-surface-overlay">
            {audit.map(event => (
              <div className="flex flex-wrap justify-between gap-2 py-3" key={event.id}>
                <div>
                  <div className="font-medium text-text-primary">
                    {event.action.replace(/\./g, ' ')}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {event.resource_type ?? 'platform'}
                    {event.resource_id ? ` · ${event.resource_id}` : ''}
                  </div>
                </div>
                <div className="text-xs text-text-tertiary">
                  {formatRecordedDate(event.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  )
}

export function PlatformTenantsPage() {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<PlatformTenant[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(
        (
          await platformApi.listTenants({
            page: 1,
            page_size: 100,
            search: search || undefined,
            status: status || undefined,
          })
        ).items
      )
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load tenants')
    } finally {
      setLoading(false)
    }
  }, [search, status])

  useEffect(() => {
    const timer = setTimeout(() => {
      void load()
    }, 300)
    return () => clearTimeout(timer)
  }, [load])

  const clearFilters = () => {
    setSearch('')
    setStatus('')
  }

  const create = async (body: Parameters<typeof platformApi.createTenant>[0]) => {
    const tenant = await platformApi.createTenant(body)
    await load()
    navigate(`/platform/tenants/${tenant.tenant_id}`)
  }
  return (
    <>
      <PageHeader
        title="Tenants"
        description={
          hasPermission('txh.platform.tenant.create')
            ? 'Create, inspect, update, suspend, and archive tenant organizations.'
            : 'Read-only tenant organization inventory.'
        }
        actions={
          hasPermission('txh.platform.tenant.create') ? (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create tenant
            </button>
          ) : undefined
        }
      />
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={event => {
          event.preventDefault()
          void load()
        }}
      >
        <input
          aria-label="Search tenants"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input input-bordered min-w-64"
          placeholder="Search by name or code…"
        />
        <select
          aria-label="Tenant status"
          className="input"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        {(search || status) && (
          <button type="button" className="btn btn-ghost" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </form>
      <State loading={loading} error={error} retry={() => void load()} />
      {!loading && !error && (
        <div className="card overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr className="border-b border-border-secondary text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                <th className="px-4 py-3.5">Tenant</th>
                <th className="px-4 py-3.5">Type</th>
                <th className="px-4 py-3.5">Plan</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-secondary/60">
              {items.map(tenant => (
                <tr
                  key={tenant.tenant_id}
                  className="hover:bg-surface-overlay/40 transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <Link className="link font-medium" to={`/platform/tenants/${tenant.tenant_id}`}>
                      {tenant.tenant_name}
                    </Link>
                    <span className="block text-xs text-text-tertiary">{tenant.tenant_code}</span>
                  </td>
                  <td className="px-4 py-3.5">{tenant.tenant_type.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3.5">{tenant.subscription?.plan_code ?? '—'}</td>
                  <td className="px-4 py-3.5">
                    <Status value={tenant.status} />
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <Link className="btn btn-sm" to={`/platform/tenants/${tenant.tenant_id}`}>
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="p-5 text-text-secondary">No tenants found.</p>}
        </div>
      )}
      {creating && <CreateTenantDialog onClose={() => setCreating(false)} onCreate={create} />}
    </>
  )
}

export function PlatformTenantDetailPage() {
  const { tenantId = '' } = useParams()
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [item, setItem] = useState<PlatformTenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [activatingTrial, setActivatingTrial] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItem(await platformApi.getTenant(tenantId))
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load tenant')
    } finally {
      setLoading(false)
    }
  }, [tenantId])
  useEffect(() => {
    void load()
  }, [load])
  const save = async (body: Record<string, unknown>) => {
    setItem(await platformApi.updateTenant(tenantId, body))
  }
  const archive = async () => {
    if (
      !item ||
      !window.confirm(
        `Archive ${item.tenant_name}? Tenant login will be disabled. This preserves audit history and is not a hard delete.`
      )
    )
      return
    try {
      setItem(await platformApi.archiveTenant(item.tenant_id))
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to archive tenant')
    }
  }
  const activateTrial = async () => {
    if (!item) return
    setActivatingTrial(true)
    setError('')
    try {
      setItem(await platformApi.activateTrial(item.tenant_id))
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to activate trial')
    } finally {
      setActivatingTrial(false)
    }
  }
  return (
    <>
      <PageHeader
        title={item?.tenant_name ?? 'Tenant'}
        description="Organization registry, lifecycle, and subscription management."
        actions={
          <>
            <button className="btn" onClick={() => navigate('/platform/tenants')}>
              Back
            </button>
            {item && hasPermission('txh.platform.tenant.update') && item.status !== 'archived' && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                Edit tenant
              </button>
            )}
            {item && hasPermission('txh.platform.tenant.archive') && item.status !== 'archived' && (
              <button className="btn btn-error btn-outline" onClick={() => setShowDelete(true)}>
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
          </>
        }
      />
      <State loading={loading} error={error} retry={() => void load()} />
      {item && (
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="card p-5">
            <h2 className="mb-4 font-semibold">Tenant details</h2>
            <dl className="grid grid-cols-[9rem_1fr] gap-3 text-sm">
              <dt className="text-text-secondary">Code</dt>
              <dd>{item.tenant_code}</dd>
              <dt className="text-text-secondary">Type</dt>
              <dd>{item.tenant_type.replace(/_/g, ' ')}</dd>
              <dt className="text-text-secondary">Status</dt>
              <dd>
                <Status value={item.status} />
              </dd>
              <dt className="text-text-secondary">Email</dt>
              <dd>{item.firm_email ?? '—'}</dd>
              <dt className="text-text-secondary">Phone</dt>
              <dd>{item.firm_phone ?? '—'}</dd>
              <dt className="text-text-secondary">Created</dt>
              <dd>{formatRecordedDate(item.created_at)}</dd>
            </dl>
          </section>
          <section className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Subscription</h2>
              {hasPermission('txh.platform.tenant.update') &&
                item.status !== 'archived' &&
                (!item.subscription || item.subscription.status === 'pending') && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => void activateTrial()}
                    disabled={activatingTrial}
                  >
                    {activatingTrial ? 'Activating…' : 'Activate 14-day trial'}
                  </button>
                )}
            </div>
            {item.subscription ? (
              <dl className="grid grid-cols-[9rem_1fr] gap-3 text-sm">
                <dt className="text-text-secondary">Plan</dt>
                <dd>{item.subscription.plan_code}</dd>
                <dt className="text-text-secondary">Status</dt>
                <dd>
                  <Status value={item.subscription.status} />
                </dd>
                <dt className="text-text-secondary">Billing cycle</dt>
                <dd>{item.subscription.billing_cycle ?? '—'}</dd>
                <dt className="text-text-secondary">Started</dt>
                <dd>{formatRecordedDate(item.subscription.starts_at)}</dd>
                <dt className="text-text-secondary">Expires</dt>
                <dd>
                  {item.subscription.expires_at
                    ? new Date(item.subscription.expires_at).toLocaleDateString()
                    : '—'}
                </dd>
                <dt className="text-text-secondary">User limit</dt>
                <dd>{item.subscription.max_users ?? '—'}</dd>
                <dt className="text-text-secondary">Client limit</dt>
                <dd>{item.subscription.max_clients ?? '—'}</dd>
              </dl>
            ) : (
              <div>
                <p className="text-sm text-text-secondary mb-3">
                  No subscription. Activate a 14-day evaluation to get started.
                </p>
              </div>
            )}
          </section>
          {hasPermission('txh.platform.user.read') && (
            <TenantUsersSection key={item.tenant_id} tenantId={item.tenant_id} />
          )}
          {hasPermission('txh.platform.tenant.archive') && item.status !== 'archived' && (
            <section className="card border border-red-200 p-5 lg:col-span-2">
              <h2 className="font-semibold text-red-700">Archive tenant</h2>
              <p className="my-2 text-sm text-text-secondary">
                Soft-delete the tenant while retaining records required for audit and recovery.
              </p>
              <button className="btn btn-error" onClick={() => void archive()}>
                Archive tenant
              </button>
            </section>
          )}
        </div>
      )}
      {editing && item && (
        <EditTenantDialog tenant={item} onClose={() => setEditing(false)} onSave={save} />
      )}
      {showDelete && item && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setShowDelete(false)}
          onConfirm={async (_mode: DeleteMode) => {
            await archive()
          }}
          entityName={item.tenant_name}
          entityType="tenant"
          confirmationPhrase={item.tenant_name}
          confirmationLabel="tenant name"
          showArchiveOption={false}
          warningText="This tenant will be archived for governed recovery. Permanent cleanup is available only from Deletion Governance after retention and preflight."
        />
      )}
    </>
  )
}

export function PlatformUsersPage() {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<PlatformUser[]>([])
  const [tenants, setTenants] = useState<PlatformTenant[]>([])
  const [search, setSearch] = useState('')
  const [active, setActive] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [tenantFilter, setTenantFilter] = useState('')
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const isPlatformOnly = tenantFilter === 'platform'
      const [users, tenantPage] = await Promise.all([
        platformApi.listUsers({
          page: 1,
          page_size: 100,
          search: search || undefined,
          is_active: active === '' ? undefined : active === 'true',
          user_category: isPlatformOnly ? 'system' : undefined,
          tenant_id: !isPlatformOnly && tenantFilter ? tenantFilter : undefined,
        }),
        platformApi.listTenants({ page: 1, page_size: 100 }),
      ])
      setItems(users.items)
      setTenants(tenantPage.items)
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load users')
    } finally {
      setLoading(false)
    }
  }, [search, active, tenantFilter])

  useEffect(() => {
    const timer = setTimeout(() => {
      void load()
    }, 300)
    return () => clearTimeout(timer)
  }, [load])

  const clearFilters = () => {
    setSearch('')
    setActive('')
    setTenantFilter('')
  }

  const create = async (body: Parameters<typeof platformApi.createUser>[0]) => {
    const user = await platformApi.createUser(body)
    await load()
    navigate(`/platform/users/${user.id}`)
  }
  return (
    <>
      <PageHeader
        title="Users"
        description={
          hasPermission('txh.platform.user.create')
            ? 'Create and manage platform identities and tenant accounts.'
            : 'Read-only platform and tenant identity inventory.'
        }
        actions={
          hasPermission('txh.platform.user.create') ? (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create user
            </button>
          ) : undefined
        }
      />
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={event => {
          event.preventDefault()
          void load()
        }}
      >
        <input
          aria-label="Search users"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input input-bordered min-w-64"
          placeholder="Search by name, email, or username…"
        />
        <select
          aria-label="User status"
          className="input"
          value={active}
          onChange={e => setActive(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <select
          aria-label="Tenant filter"
          className="input min-w-48"
          value={tenantFilter}
          onChange={e => setTenantFilter(e.target.value)}
        >
          <option value="">All scopes (Platform & Tenants)</option>
          <option value="platform">Platform users only</option>
          <optgroup label="Tenants">
            {tenants.map(t => (
              <option key={t.tenant_id} value={t.tenant_id}>
                {t.tenant_name}
              </option>
            ))}
          </optgroup>
        </select>
        {(search || active || tenantFilter) && (
          <button type="button" className="btn btn-ghost" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </form>
      <State loading={loading} error={error} retry={() => void load()} />
      {!loading && !error && (
        <div className="card overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr className="border-b border-border-secondary text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                <th className="px-4 py-3.5">User</th>
                <th className="px-4 py-3.5">Scope</th>
                <th className="px-4 py-3.5">Role</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-secondary/60">
              {items.map(user => (
                <tr key={user.id} className="hover:bg-surface-overlay/40 transition-colors">
                  <td className="px-4 py-3.5">
                    <Link className="link font-medium" to={`/platform/users/${user.id}`}>
                      {user.full_name || user.email || user.username}
                    </Link>
                    <span className="block text-xs text-text-tertiary">{user.email}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    {user.tenant_id
                      ? (tenants.find(tenant => tenant.tenant_id === user.tenant_id)?.tenant_name ??
                        'Tenant')
                      : 'Platform'}
                  </td>
                  <td className="px-4 py-3.5">
                    {user.roles.map(role => getRoleLabel(role as CanonicalUserRole)).join(', ') ||
                      'Unassigned'}
                  </td>
                  <td className="px-4 py-3.5">
                    <Status value={recordedBooleanLabel(user.is_active, 'active', 'inactive')} />
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <Link className="btn btn-sm" to={`/platform/users/${user.id}`}>
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="p-5 text-text-secondary">No users found.</p>}
        </div>
      )}
      {creating && (
        <CreateUserDialog tenants={tenants} onClose={() => setCreating(false)} onCreate={create} />
      )}
    </>
  )
}

export function PlatformUserDetailPage() {
  const { userId = '' } = useParams()
  const { hasPermission, user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [item, setItem] = useState<PlatformUser | null>(null)
  const [sessions, setSessions] = useState<PlatformUserSession[]>([])
  const [role, setRole] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [editing, setEditing] = useState(false)
  const [showDeleteUser, setShowDeleteUser] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const user = await platformApi.getUser(userId)
      setItem(user)
      setRole(user.roles[0] ?? '')
      const page = await platformApi.listUserSessions(userId)
      setSessions(page.items)
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load user')
    } finally {
      setLoading(false)
    }
  }, [userId])
  useEffect(() => {
    void load()
  }, [load])
  const save = async (body: Record<string, unknown>) =>
    setItem(await platformApi.updateUser(userId, body))
  const assign = async () => {
    try {
      setItem(await platformApi.assignRole(userId, role))
      setNotice('Role updated. New permissions apply on the user’s next login.')
      setSessions([])
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to assign role')
    }
  }
  const deactivate = async () => {
    if (
      !item ||
      !window.confirm(`Deactivate ${item.full_name || item.email}? All sessions will be revoked.`)
    )
      return
    try {
      setItem(await platformApi.deactivateUser(item.id))
      setSessions([])
      setNotice('User deactivated and sessions revoked.')
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to deactivate user')
    }
  }
  const activate = async () => {
    try {
      setItem(await platformApi.updateUser(userId, { is_active: true }))
      setNotice('User reactivated.')
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to reactivate user')
    }
  }
  const revoke = async () => {
    if (!window.confirm('Revoke every active session for this user?')) return
    try {
      await platformApi.revokeSessions(userId)
      setSessions([])
      setNotice('All sessions revoked.')
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to revoke sessions')
    }
  }
  const allowedRoles = useMemo(
    () =>
      item?.tenant_id
        ? CANONICAL_ROLES.filter(value => !value.startsWith('cml_'))
        : CANONICAL_ROLES.filter(value => value.startsWith('cml_')),
    [item?.tenant_id]
  )
  return (
    <>
      <PageHeader
        title={item?.full_name || item?.email || 'User'}
        description="Identity, role, verification, status, and active-session management."
        actions={
          <>
            <button className="btn" onClick={() => navigate('/platform/users')}>
              Back
            </button>
            {item && hasPermission('txh.platform.user.update') && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                Edit user
              </button>
            )}
            {item &&
              hasPermission('txh.platform.user.deactivate') &&
              item.id !== currentUser?.id && (
                <button
                  className="btn btn-error btn-outline"
                  onClick={() => setShowDeleteUser(true)}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
          </>
        }
      />
      <State loading={loading} error={error} retry={() => void load()} />
      {notice && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">{notice}</div>
      )}
      {item && (
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="card p-5">
            <h2 className="mb-4 font-semibold">Account</h2>
            <dl className="grid grid-cols-[8rem_1fr] gap-3 text-sm">
              <dt className="text-text-secondary">Username</dt>
              <dd>{item.username ?? '—'}</dd>
              <dt className="text-text-secondary">Email</dt>
              <dd>{item.email ?? '—'}</dd>
              <dt className="text-text-secondary">Scope</dt>
              <dd>{item.tenant_id ? `Tenant ${item.tenant_id}` : 'Platform'}</dd>
              <dt className="text-text-secondary">Verified</dt>
              <dd>{recordedBooleanLabel(item.is_verified, 'Yes', 'No')}</dd>
              <dt className="text-text-secondary">Status</dt>
              <dd>
                <Status value={recordedBooleanLabel(item.is_active, 'active', 'inactive')} />
              </dd>
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              {hasPermission('txh.platform.user.deactivate') &&
                item.is_active &&
                item.id !== currentUser?.id && (
                  <button className="btn btn-error" onClick={() => void deactivate()}>
                    Deactivate user
                  </button>
                )}
              {hasPermission('txh.platform.user.update') && !item.is_active && (
                <button className="btn btn-primary" onClick={() => void activate()}>
                  Reactivate user
                </button>
              )}
            </div>
          </section>
          <section className="card p-5">
            <h2 className="mb-4 font-semibold">Role assignment</h2>
            <p className="mb-3 text-sm text-text-secondary">
              Platform roles remain tenantless; tenant roles remain bound to the existing tenant.
            </p>
            <div className="flex gap-2">
              <select
                aria-label="Role"
                className="input flex-1"
                disabled={!hasPermission('txh.platform.user.role.assign')}
                value={role}
                onChange={e => setRole(e.target.value)}
              >
                {allowedRoles.map(value => (
                  <option value={value} key={value}>
                    {getRoleLabel(value)}
                  </option>
                ))}
              </select>
              {hasPermission('txh.platform.user.role.assign') && (
                <button className="btn btn-primary" disabled={!role} onClick={() => void assign()}>
                  Assign
                </button>
              )}
            </div>
          </section>
          <section className="card p-5 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Active sessions</h2>
                <p className="text-sm text-text-secondary">
                  {sessions.length} active session{sessions.length === 1 ? '' : 's'}
                </p>
              </div>
              {hasPermission('txh.platform.user.session.revoke') && sessions.length > 0 && (
                <button className="btn btn-error" onClick={() => void revoke()}>
                  Revoke all
                </button>
              )}
            </div>
            {sessions.length === 0 ? (
              <p className="text-sm text-text-secondary">No active sessions.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Last activity</th>
                      <th>IP</th>
                      <th>Client</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(session => (
                      <tr key={session.session_id}>
                        <td>{formatRecordedDate(session.created_at)}</td>
                        <td>
                          {session.last_activity_at
                            ? new Date(session.last_activity_at).toLocaleString()
                            : '—'}
                        </td>
                        <td>{session.ip_address ?? '—'}</td>
                        <td className="max-w-xs truncate">{session.user_agent ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
      {editing && item && (
        <EditUserDialog user={item} onClose={() => setEditing(false)} onSave={save} />
      )}
      {showDeleteUser && item && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setShowDeleteUser(false)}
          onConfirm={async (_mode, reason) => {
            await platformApi.deleteUser(item.id, reason)
            navigate('/platform/users')
          }}
          entityName={item.full_name || item.email || 'User'}
          entityType="user"
          confirmationPhrase={item.email || item.username || ''}
          confirmationLabel="email address"
          showArchiveOption={false}
          warningText="This will deactivate the user and retain the account for governed recovery."
        />
      )}
    </>
  )
}

export function PlatformAuditPage() {
  const [items, setItems] = useState<PlatformAuditEvent[]>([])
  const [action, setAction] = useState('')
  const [tenant, setTenant] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(
        (
          await platformApi.listAudit({
            page: 1,
            page_size: 100,
            action: action || undefined,
            tenant_id: tenant || undefined,
          })
        ).items
      )
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load platform audit')
    } finally {
      setLoading(false)
    }
  }, [action, tenant])
  useEffect(() => {
    void load()
  }, [])
  return (
    <>
      <PageHeader
        title="Platform Audit"
        description="Sanitized, immutable platform administration history."
        actions={
          <button className="btn" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        }
      />
      <form
        className="mb-4 flex flex-wrap gap-2"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void load()
        }}
      >
        <input
          aria-label="Audit action"
          className="input min-w-64"
          placeholder="Exact action, e.g. platform.user.created"
          value={action}
          onChange={e => setAction(e.target.value)}
        />
        <input
          aria-label="Audit tenant"
          className="input min-w-64"
          placeholder="Tenant UUID"
          value={tenant}
          onChange={e => setTenant(e.target.value)}
        />
        <button className="btn">Apply filters</button>
      </form>
      <State loading={loading} error={error} retry={() => void load()} />
      {!loading && !error && (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Target</th>
                <th>Result</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map(event => (
                <tr key={event.id}>
                  <td className="whitespace-nowrap">{formatRecordedDate(event.created_at)}</td>
                  <td className="font-medium">{event.action}</td>
                  <td className="max-w-40 truncate" title={event.actor_id}>
                    {event.actor_id ?? 'System'}
                  </td>
                  <td>
                    {event.resource_type ?? '—'}
                    {event.resource_id && (
                      <span className="block max-w-40 truncate text-xs" title={event.resource_id}>
                        {event.resource_id}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        event.success === true
                          ? 'text-green-700 dark:text-green-400'
                          : event.success === false
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-text-tertiary'
                      }
                    >
                      {recordedBooleanLabel(event.success, 'Success', 'Failed')}
                    </span>
                  </td>
                  <td>
                    <details>
                      <summary className="cursor-pointer">View</summary>
                      <pre className="mt-2 max-w-md overflow-auto whitespace-pre-wrap text-xs">
                        {JSON.stringify(event.details, null, 2)}
                      </pre>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <p className="p-5 text-text-secondary">No platform events found.</p>
          )}
        </div>
      )}
    </>
  )
}

export function PlatformPendingPage({ title, phase }: { title: string; phase: string }) {
  return (
    <>
      <PageHeader
        title={title}
        description={`${phase} is intentionally unavailable until its safety controls are implemented.`}
      />
      <div className="card p-6 text-text-secondary">
        This route is permission-gated and contains no operational shortcut.
      </div>
    </>
  )
}
